import { inject, Injectable, PendingTasks } from '@angular/core';

import type { Translation, TranslocoLoader } from '@jsverse/transloco';
import { from, type Observable } from 'rxjs';

import { getSafeLanguageCode, type SupportedLanguageCode } from './languages';

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  readonly #pendingTasks = inject(PendingTasks);

  getTranslation(lang: SupportedLanguageCode): Observable<Translation> {
    return from(this.#loadTranslation(getSafeLanguageCode(lang)));
  }

  /**
   * Static imports are not usable here: the active language is chosen at runtime and each
   * dictionary is ~120 KB, so they must stay out of the initial bundle.
   *
   * The load is registered in PendingTasks because the app renders zoneless — on the server
   * stability is tracked through PendingTasks alone, and an untracked load would let SSR
   * serialize raw translation keys instead of text.
   */
  async #loadTranslation(lang: SupportedLanguageCode): Promise<Translation> {
    const taskDone = this.#pendingTasks.add();

    try {
      switch (lang) {
        case 'en': return (await import('@rpgc-ai/i18n/en')).en;
        case 'ru': return (await import('@rpgc-ai/i18n/ru')).ru;
        case 'ua': return (await import('@rpgc-ai/i18n/ua')).ua;
        case 'de': return (await import('@rpgc-ai/i18n/de')).de;
        case 'pt': return (await import('@rpgc-ai/i18n/pt')).pt;
      }
    } finally {
      taskDone();
    }
  }
}
